import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useMemo, useEffect } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import type { ThemeItem } from 'tripPlanner/hooks/api/tripGenerationData'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { trackClientEvent } from 'analytics/analytics'

const LoadingContainer = styled(VerticalSpacer)`
  padding-block: ${rem(24)};
`

interface Props {
  themes: Array<ThemeItem> | undefined
  themesLoading: boolean
  selectedThemeNames: Array<string>
  onThemeToggle: (theme: string) => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripThemeSelection(props: Props) {
  const {
    themes,
    themesLoading,
    selectedThemeNames,
    onThemeToggle,
    isGeneratingTrip,
    stepNumber,
    totalSteps,
  } = props
  const intl = useIntl()
  const selectedThemeSet = useMemo(() =>
    new Set(selectedThemeNames),
  [selectedThemeNames])

  const fallBackThemes: Array<ThemeItem> = [
    {
      theme: intl.formatMessage({
        id: '4qpXNzxgahcS3fydqQ9z',
        defaultMessage: 'Tourist highlights',
        description: 'Theme option for tourist highlights on the tour offer detail page',
      }),
      description: '⭐',
    },
    {
      theme: intl.formatMessage({
        id: 'F8NBaKTBm0l6Yiqh97fR',
        defaultMessage: 'Beach days',
        description: 'Selectable theme option for beach days activities in the trip planner preferences',
      }),
      description: '🏖️',
    },
    {
      theme: intl.formatMessage({
        id: 'Z9AgttVOBT73qoZxQMUl',
        defaultMessage: 'Coastal walks',
        description: 'Theme option for coastal walks in the trip planner setup wizard',
      }),
      description: '🌊',
    },
    {
      theme: intl.formatMessage({
        id: 'Q-1D1c2pBSmo2UahzeCr',
        defaultMessage: 'Harbour cruises',
        description: 'Theme option for harbour cruises in the cruise offer or booking section',
      }),
      description: '🚢',
    },
    {
      theme: intl.formatMessage({
        id: 'JObYCsjwtYMQ2Aaj-8Uu',
        defaultMessage: 'Local food & wine',
        description: 'Theme option for local food and wine in the trip planner setup wizard',
      }),
      description: '🍷',
    },
    {
      theme: intl.formatMessage({
        id: 'Twg1mQy7vA2dbdKTfCyc',
        defaultMessage: 'Outdoor adventure',
        description: 'Theme option for outdoor adventure in the trip planner setup wizard',
      }),
      description: '🥾',
    },
    {
      theme: intl.formatMessage({
        id: 'iLRQ8u1YObrgZYm7A8gR',
        defaultMessage: 'Arts & Museums',
        description: 'Theme option for arts and museums in the trip planner setup wizard',
      }),
      description: '🖼️',
    },
    {
      theme: intl.formatMessage({
        id: 'm7aspXgy1mvhHj--vufo',
        defaultMessage: 'Neighbourhood exploring',
        description: 'Theme option for neighbourhood exploring in the trip planner setup wizard',
      }),
      description: '🗺️',
    },
    {
      theme: intl.formatMessage({
        id: 'PZcAaXDr2V_aiTHk439p',
        defaultMessage: 'Shopping',
        description: 'Selectable theme option for shopping activities in the trip planner preferences',
      }),
      description: '🛍️',
    },
  ]

  useEffect(() => {
    trackClientEvent({
      subject: 'themes',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const stepText = intl.formatMessage(
    {
      id: 'ewX_UMLdx4cN8zd8qwrw',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'vl4bZbPKCdI_sTy4oX-o',
      defaultMessage: 'Trip <i>Vibes</i>',
      description: 'Heading text with italicized Vibes in the trip planner feature',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'Lrq3Q8cGhMFB9d3uu0Vr',
    defaultMessage: 'Pick the experiences that excite you! Let’s set the vibe just right for you.',
    description: 'Description text encouraging users to select trip themes',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>

      {themesLoading && (
        <LoadingContainer gap={24}>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
        </LoadingContainer>
      )}

      {!themesLoading && (
        <TileCardsGroup>
          {(themes ?? fallBackThemes).map((item) => (
            <TileCard
              key={item.theme}
              title={item.theme}
              icon={item.description}
              value={item.theme}
              onClick={onThemeToggle}
              selected={selectedThemeSet.has(item.theme)}
              disabled={isGeneratingTrip}
            />
          ))}
        </TileCardsGroup>
      )}
    </Group>
  )
}

export default AITripThemeSelection
